/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Building, Users, Search, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const STAGE_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'Introduction Made': 'bg-cyan-100 text-cyan-700',
  'Email Acknowledged': 'bg-lime-100 text-lime-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'Asset Under Evaluation': 'bg-amber-100 text-amber-700',
  'Meeting Requested': 'bg-violet-50 text-violet-600',
  'Meeting Scheduled': 'bg-violet-100 text-violet-700',
  'CDA in Progress': 'bg-blue-50 text-blue-600',
  'CDA Signed': 'bg-blue-100 text-blue-700',
  'Due Diligence': 'bg-indigo-100 text-indigo-700',
  'Deal Discussion': 'bg-purple-100 text-purple-700',
  'Closed - Successful': 'bg-emerald-100 text-emerald-700',
  'Declined': 'bg-rose-100 text-rose-700',
  'Awaiting Trial Data': 'bg-orange-100 text-orange-700',
};

function CopyableEmail({ email }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={copy} className="flex items-center gap-1 group text-left max-w-full" title="Click to copy" data-testid={`copy-email-${email}`}>
      <span className="text-[12.5px] text-slate-500 truncate group-hover:text-violet-600 transition-colors" style={{ fontFamily: "Calibri, 'Segoe UI', sans-serif" }}>{email}</span>
      {copied
        ? <Check className="w-3 h-3 text-emerald-500 shrink-0" />
        : <Copy className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
      }
    </button>
  );
}

function OutreachTable({ entries }) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-10 text-sm text-slate-400">
        No entries found
      </div>
    );
  }

  // Merge entries by biotech_company_id so two "Meeting Scheduled" rows at the
  // same biotech (e.g. IDNS / Alcon / Anlong with two contacts each) collapse
  // into one row with all evaluators / emails combined.
  const groups = [];
  const byKey = new Map();
  entries.forEach(e => {
    const key = e.biotech_company_id || e.biotech_company_name || e.id;
    if (byKey.has(key)) byKey.get(key).push(e);
    else { const arr = [e]; byKey.set(key, arr); groups.push(arr); }
  });
  groups.sort((a, b) => new Date(b[0].updated_at || b[0].created_at || 0) - new Date(a[0].updated_at || a[0].created_at || 0));

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-[1.2fr_1.1fr_1.3fr_1.4fr_0.5fr] px-5 py-3 bg-slate-50 border-b border-slate-200">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Biotech Company</span>
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Evaluator</span>
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Contacts</span>
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Emails</span>
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Updated</span>
      </div>
      <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
        {groups.map((group) => {
          const head = group[0];
          const totalEntries = group.length;
          // Collect all emails + dedupe
          const emailSet = new Set();
          group.forEach(e => {
            (e.all_emails?.length > 0 ? e.all_emails : e.contact_email ? [e.contact_email] : [])
              .forEach(em => { if (em) emailSet.add(em); });
          });
          const emails = [...emailSet];
          // Unique pharma companies (the evaluator side)
          const pharmaSet = new Set();
          group.forEach(e => { if (e.company_name) pharmaSet.add(e.company_name); });
          const pharmaCompanies = [...pharmaSet];
          // Collect individual contact names (with designation hint if available)
          const contactSet = new Map();
          group.forEach(e => {
            const key = (e.contact_name || '').trim();
            if (key && !contactSet.has(key)) {
              contactSet.set(key, { name: e.contact_name, designation: e.designation || '' });
            }
          });
          const contacts = [...contactSet.values()];
          const latest = group.reduce((acc, e) => {
            const t = new Date(e.updated_at || e.created_at || 0).getTime();
            return t > acc ? t : acc;
          }, 0);

          return (
            <div
              key={head.id}
              className="grid grid-cols-[1.2fr_1.1fr_1.3fr_1.4fr_0.5fr] px-5 py-3.5 items-start hover:bg-slate-50/80 transition-colors"
              data-testid={`outreach-entry-${head.id}`}
            >
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-slate-800 truncate leading-snug flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <span className="truncate">{head.biotech_company_name || '—'}</span>
                  {totalEntries > 1 && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 shrink-0">
                      {totalEntries} contacts
                    </span>
                  )}
                </p>
                <p className="text-[11.5px] text-slate-400 truncate leading-snug mt-0.5" style={{ fontFamily: "Calibri, 'Segoe UI', sans-serif" }}>{head.asset_name || '—'}</p>
              </div>
              <div className="min-w-0 space-y-0.5">
                {pharmaCompanies.map((p, i) => (
                  <p key={i} className="text-[13px] font-medium text-slate-700 truncate leading-snug" style={{ fontFamily: 'Manrope, sans-serif' }}>{p}</p>
                ))}
              </div>
              <div className="min-w-0 space-y-1">
                {contacts.length > 0 ? contacts.map((c, i) => (
                  <div key={i} className="min-w-0">
                    <p className="text-[12.5px] font-medium text-slate-700 truncate leading-snug" style={{ fontFamily: 'Manrope, sans-serif' }}>{c.name}</p>
                    {c.designation && (
                      <p className="text-[11px] text-slate-400 truncate leading-snug" style={{ fontFamily: "Calibri, 'Segoe UI', sans-serif" }}>{c.designation}</p>
                    )}
                  </div>
                )) : (
                  <span className="text-[12px] text-slate-300">—</span>
                )}
              </div>
              <div className="min-w-0 space-y-1">
                {emails.length > 0 ? emails.map((email, i) => (
                  <CopyableEmail key={i} email={email} />
                )) : (
                  <span className="text-[12px] text-slate-300">—</span>
                )}
              </div>
              <div>
                <p className="text-[12px] text-slate-400 leading-snug" style={{ fontFamily: "Calibri, 'Segoe UI', sans-serif" }}>
                  {latest ? new Date(latest).toLocaleDateString() : '—'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OutreachStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!status) return;
    setLoading(true);
    axios.get(`${API}/outreach?status=${encodeURIComponent(status)}&limit=5000`, { withCredentials: true })
      .then(res => {
        const sorted = (res.data || []).sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
        setEntries(sorted);
      })
      .catch(() => toast.error('Failed to load outreach entries'))
      .finally(() => setLoading(false));
  }, [status]);

  // Investor module deactivated (Feb 2026): investor contacts are
  // filtered out of every outreach view — even "All".
  const pharma = entries.filter(e => e.contact_type !== 'Investor');

  const filterBySearch = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(e =>
      (e.company_name || '').toLowerCase().includes(q) ||
      (e.contact_name || '').toLowerCase().includes(q) ||
      (e.biotech_company_name || '').toLowerCase().includes(q) ||
      (e.asset_name || '').toLowerCase().includes(q)
    );
  };

  // Conversation-level counts (raw rows = one asset × pharma email thread)
  // so the tab badges match the dashboard chip 1:1. The table still groups
  // by biotech for readability — that's a display choice, not a count
  // semantic. We surface both numbers on the page header for transparency.
  const conversationCount = (list) => list.length;
  const uniqueBiotechCount = (list) => {
    const s = new Set();
    list.forEach(e => s.add(e.biotech_company_id || e.biotech_company_name || e.id));
    return s.size;
  };

  const displayEntries = activeTab === 'pharma' ? filterBySearch(pharma) : filterBySearch(pharma);

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="outreach-status-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-700" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {status}
          </h1>
          <Badge className={`text-sm px-3 py-1 ${STAGE_COLORS[status] || 'bg-slate-100 text-slate-600'}`} title="Total conversations (matches dashboard chip)">
            {conversationCount(entries)}
          </Badge>
          <span className="text-xs text-slate-400" data-testid="conversation-summary">
            {conversationCount(entries)} conversation{conversationCount(entries) === 1 ? '' : 's'} · {uniqueBiotechCount(entries)} biotech{uniqueBiotechCount(entries) === 1 ? '' : 's'}
          </span>
        </div>
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search company, contact, asset..."
            className="pl-9 text-sm"
            data-testid="status-search"
          />
        </div>
      </div>

      {/* Tabs: All / Pharma */}
      <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
        {[
          { key: 'all', label: 'All', count: conversationCount(entries), icon: null },
          { key: 'pharma', label: 'Pharma', count: conversationCount(pharma), icon: Building },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.icon && <tab.icon className="w-3.5 h-3.5" />}
            {tab.label}
            <Badge variant="secondary" className="text-[10px] ml-1">{tab.count}</Badge>
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <OutreachTable entries={displayEntries} />
      )}
    </div>
  );
}
